import { useEffect, useState } from 'react';
import type { ClientFinancialOverview, PeriodRow } from '@/core/models/practice.models';
import { practiceService } from '@/core/services/practice';
import './ClientFinancialOverview.scss';

/** Whole pounds, a negative with a minus sign: "£842,300", "-£1,200". */
const pounds = (value: number) => {
  const whole = Math.round(Math.abs(value)).toLocaleString('en-GB');
  return `${value < 0 && Math.round(value) !== 0 ? '-' : ''}£${whole}`;
};

interface Figure {
  key: 'turnover' | 'totalIncome' | 'costOfSales' | 'expenses';
  label: string;
  value: number;
}

const figuresOf = (data: ClientFinancialOverview): Figure[] => [
  { key: 'turnover', label: 'Turnover', value: data.turnover },
  { key: 'totalIncome', label: 'Total income', value: data.totalIncome },
  { key: 'costOfSales', label: 'Cost of sales', value: data.costOfSales },
  { key: 'expenses', label: 'Expenses', value: data.expenses },
];

const SIZE = 130;
const RADIUS = 47;
const CIRCUMFERENCE = 2 * Math.PI * RADIUS;

/** Total income split into cost of sales, expenses and what is left (shown only when it is positive). */
function Donut({ data }: { data: ClientFinancialOverview }) {
  const profit = data.totalIncome - data.costOfSales - data.expenses;
  const slices = [
    { key: 'costOfSales', label: 'Cost of sales', value: data.costOfSales },
    { key: 'expenses', label: 'Expenses', value: data.expenses },
    ...(profit > 0 ? [{ key: 'profit', label: 'Profit', value: profit }] : []),
  ].filter((slice) => slice.value > 0);
  const total = slices.reduce((sum, slice) => sum + slice.value, 0);
  let offset = 0;
  return (
    <div className="fo-donut-wrap">
      <svg
        className="fo-donut"
        viewBox={`0 0 ${SIZE} ${SIZE}`}
        role="img"
        aria-label={`Total income ${pounds(data.totalIncome)}`}
      >
        <circle className="fo-donut-track" cx={SIZE / 2} cy={SIZE / 2} r={RADIUS} fill="none" />
        <g transform={`rotate(-90 ${SIZE / 2} ${SIZE / 2})`}>
          {slices.map((slice) => {
            const length = (slice.value / total) * CIRCUMFERENCE;
            const circle = (
              <circle
                key={slice.key}
                className={`fo-slice fo-slice-${slice.key}`}
                cx={SIZE / 2}
                cy={SIZE / 2}
                r={RADIUS}
                fill="none"
                strokeDasharray={`${Math.max(0, length - 1.5)} ${CIRCUMFERENCE - length + 1.5}`}
                strokeDashoffset={-offset}
              >
                <title>{`${slice.label}: ${pounds(slice.value)}`}</title>
              </circle>
            );
            offset += length;
            return circle;
          })}
        </g>
        <text className="fo-donut-value" x={SIZE / 2} y={SIZE / 2 + 2} textAnchor="middle">
          {pounds(data.totalIncome)}
        </text>
        <text className="fo-donut-caption" x={SIZE / 2} y={SIZE / 2 + 15} textAnchor="middle">
          Total income
        </text>
      </svg>
      <ul className="fo-legend">
        <li>
          <span className="fo-dot fo-dot-turnover" />
          Turnover<b>{pounds(data.turnover)}</b>
        </li>
        <li>
          <span className="fo-dot fo-dot-costOfSales" />
          Cost of sales<b>{pounds(data.costOfSales)}</b>
        </li>
        <li>
          <span className="fo-dot fo-dot-expenses" />
          Expenses<b>{pounds(data.expenses)}</b>
        </li>
        {profit > 0 && (
          <li>
            <span className="fo-dot fo-dot-profit" />
            Profit<b>{pounds(profit)}</b>
          </li>
        )}
      </ul>
    </div>
  );
}

const BAR_LABEL = 92;
const BAR_ROW = 24;
const BAR_WIDTH = 300;

function Bars({ figures }: { figures: Figure[] }) {
  const max = Math.max(1, ...figures.map((figure) => Math.abs(figure.value)));
  const track = BAR_WIDTH - BAR_LABEL - 70;
  return (
    <svg
      className="fo-bars"
      viewBox={`0 0 ${BAR_WIDTH} ${figures.length * BAR_ROW}`}
      role="img"
      aria-label="Financial overview bars"
    >
      {figures.map((figure, index) => {
        const width = (Math.abs(figure.value) / max) * track;
        return (
          <g key={figure.key} transform={`translate(0 ${index * BAR_ROW})`}>
            <text className="fo-bar-label" x={0} y={15}>
              {figure.label}
            </text>
            <rect className={`fo-bar fo-bar-${figure.key}`} x={BAR_LABEL} y={4} width={Math.max(width, 1)} height={14} rx={4}>
              <title>{`${figure.label}: ${pounds(figure.value)}`}</title>
            </rect>
            <text className="fo-bar-value" x={BAR_LABEL + Math.max(width, 1) + 5} y={15}>
              {pounds(figure.value)}
            </text>
          </g>
        );
      })}
    </svg>
  );
}

type Load = { kind: 'loading' } | { kind: 'error' } | { kind: 'ready'; data: ClientFinancialOverview };

/**
 * APT378: Turnover, Total income, Cost of sales and Expenses of the client's latest period (or one picked from the
 * list), as cards, a donut and bars all at once (3 Nov: the client is not asked to pick a display). A period without
 * a trial balance, or an answer that cannot be read, is said in the widget itself; nothing else on the dashboard
 * depends on it.
 */
export function ClientFinancialOverviewWidget({ clientId }: { clientId: string }) {
  const [periodId, setPeriodId] = useState('');
  const [periods, setPeriods] = useState<PeriodRow[]>([]);
  const [load, setLoad] = useState<Load>({ kind: 'loading' });
  // The list of periods for the select is an extra: without it the select just offers the latest period.
  useEffect(() => {
    let current = true;
    try {
      void Promise.resolve(practiceService.getClientPeriods(clientId)).then(
        (result) => {
          if (current) setPeriods(result?.periods ?? []);
        },
        () => undefined,
      );
    } catch {
      /* no period list: only "Latest period" is offered */
    }
    return () => {
      current = false;
    };
  }, [clientId]);

  useEffect(() => {
    let current = true;
    setLoad({ kind: 'loading' });
    try {
      void Promise.resolve(practiceService.getFinancialOverview(clientId, periodId || undefined)).then(
        (data) => {
          if (current) setLoad(data ? { kind: 'ready', data } : { kind: 'error' });
        },
        () => {
          if (current) setLoad({ kind: 'error' });
        },
      );
    } catch {
      setLoad({ kind: 'error' });
    }
    return () => {
      current = false;
    };
  }, [clientId, periodId]);

  const data = load.kind === 'ready' ? load.data : null;
  return (
    <section className="dashboard-panel fo-widget" aria-label="Financial Overview" data-testid="financial-overview">
      <div className="dashboard-panel-header d-flex align-items-center justify-space-between">
        <h2 className="m-0">Financial Overview</h2>
        <div className="d-flex align-items-center gap-10">
          <select
            className="fo-period-select"
            aria-label="Period"
            value={periodId}
            onChange={(event) => setPeriodId(event.target.value)}
          >
            <option value="">Latest period</option>
            {periods.map((period) => (
              <option key={period.id} value={period.id}>
                {`${period.fromDate} - ${period.toDate}`}
              </option>
            ))}
          </select>
        </div>
      </div>
      <div className="fo-body" aria-busy={load.kind === 'loading'}>
        {load.kind === 'error' && <div className="fo-note">Financial overview is not available right now.</div>}
        {data && !data.hasData && <div className="fo-note">No trial balance for this period yet.</div>}
        {data?.hasData && (
          <>
            {(data.periodFrom || data.periodTo) && (
              <div className="fo-period">
                {`${data.periodFrom ?? ''} - ${data.periodTo ?? ''}`}
                {data.source ? ` (${data.source})` : ''}
              </div>
            )}
            <div className="fo-cards">
              {figuresOf(data).map((figure) => (
                <div key={figure.key} className={`fo-card fo-card-${figure.key}`}>
                  <div className="fo-card-label">{figure.label}</div>
                  <div className="fo-card-value">{pounds(figure.value)}</div>
                </div>
              ))}
            </div>
            <div className="fo-charts">
              <Donut data={data} />
              <Bars figures={figuresOf(data)} />
            </div>
          </>
        )}
      </div>
    </section>
  );
}
